import type { StatusInscricao } from '@/components/feature/SeloSituacao';
import type { LinhaParticipacao, Participacao } from './types';

export const STATUS_LABEL: Record<StatusInscricao, string> = {
  pendente: 'Aguardando pagamento',
  confirmada: 'Confirmada',
  isenta: 'Isenta',
  lista_espera: 'Na lista de espera',
  cancelada: 'Cancelada',
};

// Situação simplificada para eventos que vendem ingressos (pago / aguardando).
export interface SituacaoIngresso {
  rotulo: string;
  cls: string;
}

export function situacaoIngresso(status: StatusInscricao): SituacaoIngresso {
  switch (status) {
    case 'confirmada':
    case 'isenta':
      return { rotulo: 'Pago', cls: 'bg-accent-100 text-accent-900' };
    case 'pendente':
      return { rotulo: 'Aguardando', cls: 'bg-amber-100 text-amber-800' };
    case 'lista_espera':
      return { rotulo: 'Lista de espera', cls: 'bg-secondary-100 text-secondary-700' };
    default:
      return { rotulo: 'Cancelado', cls: 'bg-secondary-100 text-secondary-400' };
  }
}

// Situação do ingresso na aba "Compradores" (Pago / Aguardando Pix / Não pagou / Cortesia).
export interface SituacaoComprador {
  rotulo: string;
  cls: string;
}

export function situacaoComprador(status: StatusInscricao): SituacaoComprador {
  switch (status) {
    case 'confirmada':
      return { rotulo: 'Pago', cls: 'bg-success-100 text-success-700' };
    case 'pendente':
      return { rotulo: 'Aguardando Pix', cls: 'bg-amber-100 text-amber-800' };
    case 'isenta':
      return { rotulo: 'Cortesia', cls: 'bg-info-100 text-info-800' };
    case 'cancelada':
      return { rotulo: 'Não pagou', cls: 'bg-secondary-100 text-secondary-500' };
    case 'lista_espera':
    default:
      return { rotulo: 'Aguardando', cls: 'bg-amber-100 text-amber-800' };
  }
}

// Só quem já está pago (Pago ou Cortesia) pode receber o convite com o QR.
export function podeGerarConvite(status: StatusInscricao): boolean {
  return status === 'confirmada' || status === 'isenta';
}

// Frase do botão apagado, quando ainda não dá pra gerar o convite.
export function motivoConviteIndisponivel(status: StatusInscricao): string {
  return status === 'cancelada' ? 'Compra cancelada' : 'Ainda não pagou';
}

export function formatarDataCurta(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  return `${dia}/${mes}/${d.getFullYear()}`;
}

// "30/08, às 08:00" — usado no texto de vazio quando as inscrições ainda vão abrir.
export function formatarDataHora(iso: string): string {
  const d = new Date(iso);
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${dia}/${mes}, às ${hh}:${mm}`;
}

// "19/09 17:32" — data e hora curtas, usadas na linha do comprador.
export function formatarDataMinuto(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${dia}/${mes} ${hh}:${mm}`;
}

// Formata um valor em reais (número decimal) como dinheiro BRL.
export function formatarReais(valor: number): string {
  return Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

// Formata um valor em centavos sempre como dinheiro (inclusive R$ 0,00).
export function formatarDinheiro(centavos: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    Number(centavos) / 100,
  );
}

// Aplica máscara de dinheiro enquanto o usuário digita (digitos viram centavos).
export function mascaraDinheiro(v: string): string {
  const digitos = v.replace(/\D/g, '');
  if (!digitos) return '';
  const centavos = parseInt(digitos, 10);
  const reais = Math.floor(centavos / 100);
  const cents = centavos % 100;
  return `R$ ${reais.toLocaleString('pt-BR')},${String(cents).padStart(2, '0')}`;
}

// Converte um valor mascarado de volta para centavos.
export function centavosDeDinheiro(v: string): number {
  const digitos = v.replace(/\D/g, '');
  return digitos ? parseInt(digitos, 10) : 0;
}

// Link de WhatsApp a partir de um telefone (assume Brasil quando falta o 55).
export function linkWhatsApp(telefone: string): string {
  const d = telefone.replace(/\D/g, '');
  if (!d) return '#';
  const numero = d.startsWith('55') ? d : `55${d}`;
  return `https://wa.me/${numero}`;
}

// Link de WhatsApp já com a mensagem pronta (codificada).
export function linkWhatsAppMensagem(telefone: string, mensagem: string): string {
  const d = (telefone ?? '').replace(/\D/g, '');
  if (!d) return '#';
  const completo = d.startsWith('55') && d.length >= 12 && d.length <= 13;
  const numero = completo ? d : `55${d}`;
  return `https://wa.me/${numero}?text=${encodeURIComponent(mensagem)}`;
}

// Data de hoje no formato YYYY-MM-DD (para campo type="date").
export function hojeISO(): string {
  const d = new Date();
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
}

function escaparCSV(campo: string | number | null | undefined): string {
  const s = campo === null || campo === undefined ? '' : String(campo);
  // Previne injeção de fórmula no Excel: células que começam com =, +, - ou @.
  const protegido = /^[=+\-@]/.test(s) ? `'${s}` : s;
  if (/[;",\n\r]/.test(protegido)) {
    return `"${protegido.replace(/"/g, '""')}"`;
  }
  return protegido;
}

// Gera e baixa um CSV com BOM e delimitador de ponto-e-vírgula (Excel pt-BR).
export function baixarCSV(nomeArquivo: string, linhas: string[][]): void {
  const conteudo = linhas.map((linha) => linha.map(escaparCSV).join(';')).join('\r\n');
  const blob = new Blob([`\uFEFF${conteudo}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nomeArquivo;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Agrupa as linhas de vw_participacoes (uma por modalidade/grupo) em participações,
// somando vagas/ocupadas dos grupos. vagas NULO = sem limite.
export function montarParticipacoes(linhas: LinhaParticipacao[]): Participacao[] {
  const mapa = new Map<number, Participacao>();
  for (const l of linhas) {
    let p = mapa.get(l.modalidade_id);
    if (!p) {
      p = {
        modalidade_id: l.modalidade_id,
        modalidade: l.modalidade,
        ordem: l.ordem_modalidade,
        vagas: null,
        ocupadas: 0,
        restantes: null,
        situacao: l.situacao,
        inscricoes_abrem_em: l.inscricoes_abrem_em,
        grupos: [],
      };
      mapa.set(l.modalidade_id, p);
    }
    p.ocupadas += Number(l.ocupadas) || 0;
    if (l.grupo) {
      p.grupos.push({
        grupo: l.grupo,
        vagas: l.vagas,
        ocupadas: Number(l.ocupadas) || 0,
        restantes: l.restantes,
      });
    } else {
      p.vagas = l.vagas;
      p.restantes = l.restantes;
    }
  }

  for (const p of mapa.values()) {
    if (p.grupos.length > 0) {
      let comLimite = true;
      let somaVagas = 0;
      let somaRestantes = 0;
      for (const g of p.grupos) {
        if (g.vagas === null || g.vagas === undefined) comLimite = false;
        else somaVagas += Number(g.vagas);
        if (g.restantes === null || g.restantes === undefined) comLimite = false;
        else somaRestantes += Number(g.restantes);
      }
      p.vagas = comLimite ? somaVagas : null;
      p.restantes = comLimite ? somaRestantes : null;
    }
  }

  return [...mapa.values()].sort((a, b) => a.ordem - b.ordem);
}